import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { reportingToolTip } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { t, type IntlService } from 'ember-intl'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { AxisLabelsFormatterContextObject, Point } from 'highcharts'
import { formatValue } from 'teacher-ui-client/utils/format-value'

export interface DrivingTestsStudentItemRe {
  summary: {
    completed_count: number
    percent_complete: number
  }
  categories: {
    category: 'letters_and_sounds' | 'content_words' | 'sight_words'
    completed: number
    percent_complete: number
    total_attempts: number
  }[]
}

export interface DrivingTestsStudentItemMs {
  driving_test_grade: number
  summary: {
    completed_count: number
    percent_complete: number
  }
  categories: {
    category: 'geometry' | 'measurement' | 'number' | 'operations' | 'patterns' | 'data'
    completed: number
    percent_complete: number
    total_attempts: number
  }[]
}

interface Args {
  data: DrivingTestsStudentItemRe | DrivingTestsStudentItemMs
  product: 're' | 'maths'
  link?: { route: string; text: string }
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DrivingTestsStudent extends Component<Signature> {
  @service intl!: IntlService

  readonly summaryTitle = 'Driving Tests'

  readonly orderedCategories = {
    maths: ['number', 'operations', 'patterns', 'geometry', 'data', 'measurement'],
    re: ['sight_words', 'letters_and_sounds', 'content_words'],
  }

  /**
   * By setting this to false, along with no set maximum `yRange` means that when a student has 100%, the y axis ticks
   * don't go beyond 100%.
   */
  readonly yAxisEndOnTick = false

  /**
   * The range of the chart
   *
   * We don't want to set 100 as the maximum here, or it will always show 0 to 100%, even if only 1% completed.
   */
  readonly yRange: [number, null] = [0, null]

  get categories() {
    return this.orderedCategories[this.args.product].map((category) => {
      return this.intl.t(`driving_tests.${this.args.product}.categories.${category}`)
    })
  }

  get colors() {
    const { product } = this.args
    return this.orderedCategories[product].map((category) => {
      switch (`${product}.${category}`) {
        case 'maths.geometry':
          return colors.yolkyYellow300
        case 'maths.data':
          return colors.watermelonyRed300
        case 'maths.measurement':
          return colors.wlBlue250
        case 'maths.number':
        case 're.sight_words':
          return colors.oceanyBlue300
        case 'maths.operations':
        case 're.letters_and_sounds':
          return colors.forestGreen300
        case 'maths.patterns':
        case 're.content_words':
          return colors.purplyPink300
        default:
          return colors.oceanyBlue300
      }
    })
  }

  get colorScheme() {
    if (this.args.product === 're') return SummaryBoxColorScheme.DarkBlue
    return SummaryBoxColorScheme.LightBlue
  }

  get chartData(): SeriesColumnOptions[] {
    return [
      {
        data: this.orderedCategories[this.args.product].map((category) => {
          return this.args.data.categories?.find((c) => c.category === category)?.percent_complete ?? 0
        }),
        colorByPoint: true,
        colors: this.colors,
        type: 'column',
      },
    ]
  }

  /**
   * The tooltip formatter needs access to more data than is available from
   * that we can pass into highcharts. We use the point.x value to look up which
   * category is being hovered over, and we use that category key to look up the
   * number of attempts, and the number of tests total for that category,
   * which allows us to build the correct tooltip information.
   */
  get toolTip() {
    const {
      data: { categories },
      product,
    } = this.args

    const { intl, orderedCategories } = this

    return reportingToolTip('x', undefined, {
      formatter(this: Point) {
        const productCategories = orderedCategories[product]
        const category = productCategories[this.x]
        const categoryData = categories?.find((categoryData) => categoryData.category === category)
        const total = categoryData?.total_attempts

        if (!categoryData) return false

        const { completed } = categoryData

        const completedText = intl.t('reporting.tooltips.drivingTests.completedTests', { count: completed })
        const totalText = intl.t('reporting.tooltips.drivingTests.totalAttempts', { count: total })

        return `${completedText}<br/>${totalText}`
      },
      backgroundColor: colors.neutral400,
    })
  }

  get summaryStats() {
    const summaryData = this.args.data.summary
    const completed = summaryData.completed_count
    const percentComplete = summaryData.percent_complete

    return [
      { label: 'Tests completed', value: completed.toString() },
      { label: 'Completed', value: formatValue(percentComplete, { append: '%' }) },
    ]
  }

  yAxisFormatter(this: AxisLabelsFormatterContextObject) {
    return `${this.value}%`
  }

  <template>
    <div data-test-giraffe-driving-tests-category data-test-giraffe-driving-tests-student class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{this.summaryTitle}}
            @stats={{this.summaryStats}}
            @link={{@link}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @categories={{this.categories}}
            @data={{this.chartData}}
            @yRange={{this.yRange}}
            @yAxisLabelFormatter={{this.yAxisFormatter}}
            @xAxisTickWidth={{0}}
            @yAxisLabel={{t "reporting.yAxisLabels.drivingTestsTotalsStudent"}}
            @yAxisEndOnTick={{this.yAxisEndOnTick}}
            @toolTip={{this.toolTip}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default DrivingTestsStudent
